import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps, DialogSize } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'
import { useToast } from '@wartek-id/toast'
import { Textarea } from '@wartek-id/textarea'

import { CardPelaksanaanStepReset } from 'app/KinerjaShared/components/CardPelaksanaanStepReset'
import { assetUrl } from 'utils/url'
import {
  API_PATH_PELAKSANAAN_PROGRESS,
  useAPIPostPelaksanaanAppealRequest,
} from 'api/kinerja/useAPIKinerja'

import type {
  TPelaksanaanAppealableStep,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'

type DialogPelaksanaanAppealRequestProps = Omit<DialogProps, 'children'> & {
  step: TPelaksanaanAppealableStep
  headerText: string
  subHeaderText?: string
  confirmationText: string
  infoText: string
  needWrittenConfirmation?: boolean
  initialDialogSize: DialogSize
  stepDeficit?: number
}

export function DialogPelaksanaanAppealRequest({
  isOpen,
  step,
  onClose,
  headerText,
  subHeaderText,
  confirmationText,
  initialDialogSize,
  infoText,
  stepDeficit,
  needWrittenConfirmation,
  ...dialogProps
}: DialogPelaksanaanAppealRequestProps) {
  const [activeContent, setActiveContent] = useState<
    'CONFIRMATION' | 'SUCCESS'
  >('CONFIRMATION')
  const toast = useToast()
  const queryClient = useQueryClient()
  const { reset, formState, register, handleSubmit } = useForm({
    mode: needWrittenConfirmation ? 'onSubmit' : 'onChange',
  })

  const mutationPelaksanaanAppealRollbackRequest =
    useAPIPostPelaksanaanAppealRequest()

  // Reset dialog upon dismissal
  useEffect(() => {
    if (!isOpen) {
      reset()
      setActiveContent('CONFIRMATION')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  let stepsToReset: Array<TStatePelaksanaan> = []
  switch (step) {
    case 'DOCUMENT_PREPARATION':
      stepsToReset = [
        'DOCUMENT_PREPARATION',
        'OBSERVATION_IMPLEMENTATION',
        'DOCUMENT_FOLLOW_UP',
        'DOCUMENT_REFLECTION_FOLLOW_UP',
      ]
      // cover form A to form C
      if (stepDeficit <= 3) {
        stepsToReset = stepsToReset.slice(0, stepDeficit)
      }
      // form C & C KS done, not yet form D
      if (stepDeficit === 4) {
        stepsToReset = stepsToReset.slice(0, 3)
      }
      break
    case 'DOCUMENT_FOLLOW_UP':
      stepsToReset = ['DOCUMENT_FOLLOW_UP', 'DOCUMENT_REFLECTION_FOLLOW_UP']
      // form C to form D deficit 2 states
      if (stepDeficit === 2) {
        stepsToReset = stepsToReset.slice(0, 1)
      }
      break
    case 'DOCUMENT_REFLECTION_FOLLOW_UP':
      stepsToReset = ['DOCUMENT_REFLECTION_FOLLOW_UP']
      break
  }

  const dialogSize: DialogSize =
    activeContent === 'SUCCESS' ? 'sm' : initialDialogSize

  const errorMessage = formState.errors?.confirmationText?.message

  const handleConfirm = () => {
    mutationPelaksanaanAppealRollbackRequest.mutate(
      { appealState: step },
      {
        onSuccess: () => {
          setActiveContent('SUCCESS')
        },
        onError: () => {
          toast({
            message: 'Gagal lanjut. Coba lagi.',
          })
        },
      }
    )
  }

  const isSubmitDisabled = needWrittenConfirmation
    ? !formState.isDirty
    : !formState.isValid

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      autoFocus={false}
      size={dialogSize}
      onClose={onClose}
      {...dialogProps}
    >
      <DialogOverlay />
      <DialogContent className="p-6">
        {activeContent === 'CONFIRMATION' && (
          <form className="space-y-4" onSubmit={handleSubmit(handleConfirm)}>
            <div>
              <Text variant="display-sm">{headerText}</Text>
              {!!subHeaderText && (
                <Text variant="body-lg" color="subdued">
                  {subHeaderText}
                </Text>
              )}
            </div>
            <div
              className={clsx(
                'grid gap-4',
                step === 'DOCUMENT_PREPARATION' ? 'grid-cols-2' : ' grid-cols-1'
              )}
            >
              {stepsToReset.map((step, i) => (
                <CardPelaksanaanStepReset
                  step={step}
                  className="max-w-md"
                  key={i}
                  isGuru
                />
              ))}
            </div>
            <Text variant="body-lg" color="subdued" className="max-w-xl">
              {infoText}
            </Text>

            {!needWrittenConfirmation && (
              <Checkbox
                {...register('confirmation', { required: 'Mohon setujui.' })}
                value="accept"
                labelPosition="start"
              >
                <Text>{confirmationText}</Text>
              </Checkbox>
            )}
            {needWrittenConfirmation && (
              <div className="max-w-xl space-y-4">
                <Text
                  variant="heading-sm"
                  color="informational"
                  className="!text-blue-60 bg-blue-5 p-1 rounded select-none"
                >
                  {confirmationText}
                </Text>
                <div className="space-y-1">
                  <Text>Kalimat persetujuan</Text>
                  <Textarea
                    rows={2}
                    isInvalid={!!errorMessage}
                    errorMessage={errorMessage}
                    {...register('confirmationText', {
                      required: true,
                      validate: (value) =>
                        value === confirmationText ||
                        'Mohon cek ejaan dan coba lagi.',
                    })}
                  />
                </div>
              </div>
            )}
            <div className="flex space-x-3 border-t pt-4 justify-end">
              <Button
                disabled={mutationPelaksanaanAppealRollbackRequest.isLoading}
                color="white"
                size="lg"
                onClick={() => onClose?.()}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                disabled={
                  isSubmitDisabled ||
                  mutationPelaksanaanAppealRollbackRequest.isLoading
                }
                isLoading={mutationPelaksanaanAppealRollbackRequest.isLoading}
                size="lg"
                type="submit"
              >
                Ajukan perubahan
              </Button>
            </div>
          </form>
        )}

        {activeContent === 'SUCCESS' && (
          <>
            <img
              src={assetUrl('/illustrations/img-time-document.png')}
              width={84}
              height={84}
              alt=""
              className="mb-6"
            />
            <Text variant="display-sm" className="mb-4">
              Perubahan sedang diajukan ke atasan
            </Text>
            <Text variant="body-lg" color="subdued" className="mb-6">
              Status pengajuannya dapat dilihat di halaman Notifikasi atau cek
              lagi halaman ini nanti.
            </Text>
            <Button
              size="lg"
              onClick={() => {
                onClose?.()
                queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
              }}
            >
              Saya mengerti
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
